// Прототип «реальных вокселей»: отдельный draw-call на куб; скрытие сабмешей
// по серверной bodyVox-сетке. Ячейка на куб задаётся один раз в bind pose
// (без FK от анимации) — иначе границы ячеек «мигают» и спамят осколки.

import { computeNodeWorldMatrices, type PoseSample } from "./animation";
import { mat4Multiply } from "./math";
import {
  BODY_VOX_CUBE,
  BODY_VOX_GRID_BYTES,
  BODY_VOX_OX,
  BODY_VOX_OY,
  BODY_VOX_OZ,
  BODY_VOX_SX,
  BODY_VOX_SY,
  BODY_VOX_SZ,
} from "./bodyVox";
import type { LoadedMesh, SubMesh } from "./meshLoader";
import type { MeshInstance } from "./renderer";
import { trySpawnRealVoxelFlyPiece } from "./realVoxFly";

/** Как в server bodyhit.WorldToBodyLocal / mesh.frag — только горизонтальный yaw актёра. */
export function worldToBodyLocal(
  px: number, py: number, footZ: number, yaw: number,
  wx: number, wy: number, wz: number,
): [number, number, number] {
  const dx = wx - px;
  const dy = wy - py;
  const cs = Math.cos(yaw);
  const sn = Math.sin(yaw);
  const lx = dx * cs + dy * sn;
  const ly = -dx * sn + dy * cs;
  const lz = wz - footZ;
  return [lx, ly, lz];
}

function bodyLocalToCell(lx: number, ly: number, lz: number): number {
  const ix = Math.floor((lx - BODY_VOX_OX) / BODY_VOX_CUBE);
  const iy = Math.floor((ly - BODY_VOX_OY) / BODY_VOX_CUBE);
  const iz = Math.floor((lz - BODY_VOX_OZ) / BODY_VOX_CUBE);
  if (ix < 0 || ix >= BODY_VOX_SX || iy < 0 || iy >= BODY_VOX_SY || iz < 0 || iz >= BODY_VOX_SZ) {
    return -1;
  }
  return ix + iy * BODY_VOX_SX + iz * BODY_VOX_SX * BODY_VOX_SY;
}

function isVoxelCarcassSubmesh(inner: LoadedMesh, sm: SubMesh): boolean {
  if (sm.bodyLayer === "inner") return true;
  const low = (inner.nodes[sm.nodeIdx]?.name ?? "").toLowerCase();
  return low.includes("voxel") || low.includes("carcass") || low.includes("blocks");
}

function cellAlive(alive: Uint8Array, cell: number): boolean {
  return (alive[cell >> 3] & (1 << (cell & 7))) !== 0;
}

const _scratchIm = new Float32Array(16);
const _scratchWorld = new Float32Array(16);

/** Референсная поза солдата — как при первом спавне (idle, ступни на полу). */
export interface RealVoxRefPose {
  x: number;
  y: number;
  z: number;
  yaw: number;
  yawOffset: number;
  scale: number;
}

/** Центроиды вершин в локале примитива (длина = subMeshes.length * 3). */
export function precomputeInnerSubmeshCentroids(inner: LoadedMesh): Float32Array {
  const out = new Float32Array(inner.subMeshes.length * 3);
  for (let si = 0; si < inner.subMeshes.length; si++) {
    const sm = inner.subMeshes[si]!;
    let cx = 0, cy = 0, cz = 0;
    let n = 0;
    const p = sm.cpuVerts.positions;
    for (let k = 0; k < p.length; k += 3) {
      cx += p[k];
      cy += p[k + 1];
      cz += p[k + 2];
      n++;
    }
    if (n > 0) {
      cx /= n;
      cy /= n;
      cz /= n;
    }
    out[si * 3] = cx;
    out[si * 3 + 1] = cy;
    out[si * 3 + 2] = cz;
  }
  return out;
}

/**
 * Один раз при загрузке: куб → индекс ячейки серверной сетки в T-pose / rest FK.
 * Совпадает с тем, как сервер резает сетку по body-local (без дрейфа от анимации).
 */
export function buildRealVoxelStableCellMap(
  inner: LoadedMesh,
  centroids: Float32Array,
  ref: RealVoxRefPose,
): Int32Array {
  const out = new Int32Array(inner.subMeshes.length).fill(-1);
  const pose: PoseSample = {
    translation: new Map(),
    rotation: new Map(),
    scale: new Map(),
  };
  const nw = new Float32Array(inner.nodes.length * 16);
  computeNodeWorldMatrices(inner.nodes, inner.nodeTraversalOrder, pose, nw);

  const sc = ref.scale;
  const fy = ref.yaw + ref.yawOffset;
  const c = Math.cos(fy);
  const s = Math.sin(fy);
  const im = _scratchIm;
  im[0] = sc * c;
  im[1] = sc * s;
  im[2] = 0;
  im[3] = 0;
  im[4] = 0;
  im[5] = 0;
  im[6] = sc;
  im[7] = 0;
  im[8] = sc * s;
  im[9] = -sc * c;
  im[10] = 0;
  im[11] = 0;
  im[12] = ref.x;
  im[13] = ref.y;
  im[14] = ref.z;
  im[15] = 1;

  const w = _scratchWorld;
  for (let si = 0; si < inner.subMeshes.length; si++) {
    const sm = inner.subMeshes[si]!;
    if (!isVoxelCarcassSubmesh(inner, sm)) continue;
    const cx = centroids[si * 3] ?? 0;
    const cy = centroids[si * 3 + 1] ?? 0;
    const cz = centroids[si * 3 + 2] ?? 0;
    const nwOff = sm.nodeIdx * 16;
    mat4Multiply(w, im, nw.subarray(nwOff, nwOff + 16));

    const wx = w[0] * cx + w[4] * cy + w[8] * cz + w[12];
    const wy = w[1] * cx + w[5] * cy + w[9] * cz + w[13];
    const wz = w[2] * cx + w[6] * cy + w[10] * cz + w[14];

    const [lx, ly, lz] = worldToBodyLocal(ref.x, ref.y, ref.z, ref.yaw, wx, wy, wz);
    out[si] = bodyLocalToCell(lx, ly, lz);
  }
  return out;
}

/** Обновляет realVoxelHiddenSub + при переходе «ячейка жила → мертва» отрывает куб в realVoxelFlying. */
export function updateRealVoxelSubmeshHidden(
  inst: MeshInstance,
  stableCell: Int32Array,
  centroids: Float32Array,
  timeAcc: number,
): void {
  const inner = inst.innerMesh;
  const hidden = inst.realVoxelHiddenSub;
  const alive = inst.bodyVoxAlive;
  if (!inner || !inst.realVoxelBody || !hidden || hidden.length !== inner.subMeshes.length) return;
  if (stableCell.length !== inner.subMeshes.length) return;
  if (!alive || alive.length !== BODY_VOX_GRID_BYTES) {
    hidden.fill(0);
    return;
  }

  const prev = inst.realVoxelCellAlivePrev;
  if (!prev || prev.length !== inner.subMeshes.length) {
    inst.realVoxelCellAlivePrev = new Uint8Array(inner.subMeshes.length);
    const pp = inst.realVoxelCellAlivePrev;
    for (let si = 0; si < inner.subMeshes.length; si++) {
      const sm = inner.subMeshes[si]!;
      if (!isVoxelCarcassSubmesh(inner, sm)) {
        pp[si] = 0;
        hidden[si] = 0;
        continue;
      }
      const cell = stableCell[si];
      if (cell < 0) {
        pp[si] = 0;
        hidden[si] = 0;
        continue;
      }
      const aliveNow = cellAlive(alive, cell);
      pp[si] = aliveNow ? 1 : 0;
      hidden[si] = !aliveNow ? 1 : 0;
    }
    return;
  }

  for (let si = 0; si < inner.subMeshes.length; si++) {
    const sm = inner.subMeshes[si]!;
    if (!isVoxelCarcassSubmesh(inner, sm)) {
      hidden[si] = 0;
      continue;
    }
    const cell = stableCell[si];
    if (cell < 0) {
      hidden[si] = 0;
      prev[si] = 0;
      continue;
    }
    const aliveNow = cellAlive(alive, cell);
    const wasAlive = prev[si] !== 0;
    if (wasAlive && !aliveNow) {
      trySpawnRealVoxelFlyPiece(inst, si, centroids, timeAcc);
    }
    prev[si] = aliveNow ? 1 : 0;
    hidden[si] = !aliveNow ? 1 : 0;
  }
}
